import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { pairFocusEvent } from '@root/entities/pair'
import { useAppRoutes } from '@root/features/routing'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage, SegmentControl } from '@root/shared/ui-kit'
import { CurrencyCardCompact } from '@root/widgets/fun/currency-card/ui/currency-card-compact'
import { CurrencyCardCompactSkeleton } from '@root/widgets/fun/currency-card/ui/currency-card-compact-skeleton'

import { reatomCoinsFilter } from '../atoms'
import { CoinsLaunchedTime, CoinsType } from '../model'

import type { DelugeId } from '@root/entities/user-short'

const CoinsTypeTranslate = {
  [CoinsType.Held]: 'common.coins_held',
  [CoinsType.Created]: 'common.created_coins',
} as const satisfies Record<CoinsType.CoinsType, string>

type ProfileCoinsProps = {
  delugeId: DelugeId
}

export const ProfileCoinsMobile = ({ delugeId }: ProfileCoinsProps) => {
  const { t } = useTranslation()
  const pairFocus = useAction(pairFocusEvent)
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomCoinsFilter({ delugeId }),
    [delugeId],
  )
  const { navigateToRoot } = useAppRoutes()
  const [launchedTime, setLaunchedTime] = useAtom(model.launchedTimeAtom)
  const [coinsType, setCoinsType] = useAtom(model.coinsTypeAtom)
  const [filterItemsState] = useAtom(model.filterItemsStateAtom)
  const refreshFilter = useAction(model.filterItemsStateAtom.refresh)

  const renderLaunchedTime = (x: CoinsLaunchedTime.CoinsLaunchedTime): string =>
    CoinsLaunchedTime.match
      .I(x)({
        CoinsLaunchedHours: (x) =>
          t('common.format.hours_short', { count: x.hours }),
        CoinsLaunchedDays: (x) =>
          t('common.format.days_short', { count: x.days }),
        CoinsLaunchedAll: () => t('common.all'),
      })
      .toUpperCase()

  return (
    <Root>
      <Header>
        <Filter>
          <SegmentControl value={coinsType} onChange={setCoinsType}>
            {CoinsType.all.map((coinsType) => (
              <SegmentControl.Item value={coinsType} key={coinsType}>
                {t(CoinsTypeTranslate[coinsType])}
              </SegmentControl.Item>
            ))}
          </SegmentControl>
          <SegmentControl
            value={launchedTime}
            onChange={setLaunchedTime}
            getId={CoinsLaunchedTime.id}
          >
            {model.launchedTimes.map((period) => (
              <SegmentControl.Item
                value={period}
                key={CoinsLaunchedTime.id(period)}
              >
                {renderLaunchedTime(period)}
              </SegmentControl.Item>
            ))}
          </SegmentControl>
        </Filter>
      </Header>
      <Body>
        {AsyncStateN.matchWaiting(filterItemsState, {
          Error: () => (
            <InfoContainer>
              <InfoMessage
                intent={'error'}
                variant="dt"
                onRetry={refreshFilter}
              />
            </InfoContainer>
          ),
          Waiting: () => (
            <FilterResult>
              {Array.range(0, 5).map((x) => (
                <CurrencyCardCompactSkeleton key={x} />
              ))}
            </FilterResult>
          ),
          Success: (pairs) =>
            pairs.length <= 0 ? (
              <InfoContainer>
                <InfoMessage intent={'empty'} variant="dt" />
              </InfoContainer>
            ) : (
              <FilterResult>
                {pairs.map((pair) => (
                  <CurrencyCardCompact
                    onClick={() => {
                      navigateToRoot()
                      pairFocus({
                        network: pair.network,
                        address: pair.address,
                      })
                    }}
                    pair={pair}
                    key={pair.address}
                  />
                ))}
              </FilterResult>
            ),
        })}
      </Body>
    </Root>
  )
}

const Root = styled.div`
  overflow: auto;
  position: relative;
  padding: 0 12px;
`

const Header = styled.div`
  position: sticky;
  padding: 12px 0;
  top: 0;
  z-index: 1;
  background: var(--background-1);
`

const Filter = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
`

const Body = styled.div`
  position: relative;
  z-index: 0;
`

const FilterResult = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 12px;
  justify-content: start;
`

const InfoContainer = styled.div`
  padding: 120px 0;
`
